import { alpha, Box, Button, Stack, Zoom } from "@mui/material";
import { Category } from "../../../../../domain/model/Category";
import {
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../../../../data/colors/Colors";
import { CustomCardView } from "../../../../component/CustomCardView";
import { CustomImageView } from "../../../../component/CustomImageView";
import { CustomTextView } from "../../../../component/CustomTextView";
import {
  formatEnumLabel,
  getDominantColorOfImage,
  hasEnglishLetters,
} from "../../../../../core/utils/Utils";
import { useEffect, useState } from "react";
import useWindowDimensions from "../../../../../core/utils/UseWindowDimensions";

interface props {
  category: Category;
  index?: number;
  href?: string;
  imageWidth?: number;
}

export function CategoriesListItemView(props: props) {
  const [bgColor, setBgColor] = useState<string>();
  const { width } = useWindowDimensions();
  const isMobile = width < 900;

  useEffect(() => {
    if (!props.href) {
      return;
    }
    getDominantColorOfImage(props.category.imageUrl!).then((rgb) => {
      if (rgb[0] == 0 && rgb[1] == 0 && rgb[2] == 0) {
        setBgColor(COLOR_VERY_LIGHT_GRAY);
        return;
      }
      setBgColor(`rgba(${rgb[0]},${rgb[1]},${rgb[2]}, 0.6)`);
    });
  }, [props.href]);

  return (
    <Box
      sx={{
        "&:hover": props.href
          ? {
              transform: width < 900 ? undefined : "scale3d(1.01, 1.01, 1)",
              zIndex: "1",
            }
          : {},
        transition: props.href ? "transform 0.2s ease-in-out" : undefined,
      }}
    >
      <CustomCardView
        padding={0}
        borderRadius={6}
        border="1px solid black"
        children={
          <Button
            LinkComponent={props.href ? "a" : undefined}
            fullWidth
            href={props.href}
            sx={{
              padding: 0,
              "&:hover": props.href
                ? { background: COLOR_VERY_LIGHT_GRAY }
                : {},
              borderRadius: 6,
              scale: 1.01,
            }}
            disabled={!props.href}
          >
            <Stack
              spacing={0}
              textTransform={"none"}
              width={"100%"}
              height={props.href ? 280 : undefined}
              position={"relative"}
            >
              {props.href ? (
                <Zoom in={bgColor != undefined} timeout={800}>
                  <div>
                    <CustomImageView
                      height={280}
                      width="100%"
                      scale="contain"
                      src={props.category.imageUrl}
                      props={{ background: bgColor, borderRadius: 6 }}
                    />
                  </div>
                </Zoom>
              ) : (
                <CustomImageView
                  height={isMobile ? 180 : 250}
                  width={props.imageWidth ?? 200}
                  scale="contain"
                  src={props.category.imageUrl}
                  props={{ background: bgColor, borderRadius: 6 }}
                />
              )}
              {props.index != undefined && (
                <Stack
                  position={"absolute"}
                  zIndex={3}
                  p={1}
                  sx={{
                    background: alpha(COLOR_MAIN, 0.8),
                    borderRadius: "50%",
                    width: 50,
                    height: 50,
                    alignItems: "center",
                    justifyContent: "center",
                    border: "solid 0.5px lightGray",
                  }}
                >
                  <CustomTextView
                    text={`#${props.index + 1}`}
                    fontSize={20}
                    fontWeight="300"
                    textColor={COLOR_WHITE}
                  />
                </Stack>
              )}
              <Stack
                position={"absolute"}
                zIndex={3}
                p={1}
                right={0}
                sx={{
                  background: alpha(COLOR_MAIN, 0.8),
                  borderRadius: 2,
                  border: "solid 0.5px lightGray",
                }}
              >
                {props.category.schoolTypes?.map((schoolType, index) => {
                  return (
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      spacing={0.5}
                      key={schoolType}
                    >
                      <CustomTextView
                        text={`#${index + 1}`}
                        fontSize={10}
                        textColor={COLOR_WHITE}
                      />
                      <CustomTextView
                        text={formatEnumLabel(schoolType ?? "")}
                        fontSize={12}
                        fontWeight="bold"
                        textColor={COLOR_WHITE}
                      />
                    </Stack>
                  );
                })}
              </Stack>
              <Stack position={"absolute"} bottom={0} zIndex={2} width={"100%"}>
                <Stack
                  pr={2}
                  pl={2}
                  pb={1}
                  pt={1}
                  sx={{
                    direction: hasEnglishLetters(props.category.title ?? "")
                      ? "ltr"
                      : "rtl",
                    background: alpha(COLOR_MAIN, 0.8),
                    borderRadius: 6,
                    border: "solid 0.5px lightGray",
                  }}
                  width={"100%"}
                  direction={"row"}
                  justifyContent={props.href ? "space-between" : "center"}
                >
                  <CustomTextView
                    text={props.category.title}
                    fontWeight="bold"
                    fontSize={props.href ? 22 : isMobile ? 14 : 22}
                    textColor={COLOR_WHITE}
                    props={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      overflowWrap: "break-word",
                      whiteSpace: "nowrap",
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Button>
        }
      />
    </Box>
  );
}
